/**
 * Settings route — thin wrapper over the presentational `DocumentTypeSettings`
 * admin screen (Spaces / Global library / Space-local tabs). The active tab is
 * derived from the pathname; the bare `/settings` path redirects to `/spaces`.
 * The header's primary action is tab-dependent and drives either navigation to
 * the Space Editor or the controlled "New global attribute" modal.
 */
import React, { useState } from "react";
import { useHistory, useLocation, Redirect } from "react-router-dom";
import { Button } from "@jsq/ds/core";
import {
  useAttributeDeletionImpact,
  useDeleteSpaceLocalAttribute,
  usePromotionPreflight,
} from "#common/graphql/Attribute";
import { DocumentTypeSettings } from "#components/DocumentTypeSettings";
import { isAttributeNameConflictError } from "#components/DocumentTypeSettings/errors";
import * as styles from "../styles_vanilla.css";
import {
  globalAttrPromoteErrorMessage,
  globalAttrPromoteSuccessMessage,
  globalAttrSaveErrorMessage,
  globalAttrSaveSuccessMessage,
  spaceLocalAttrSaveErrorMessage,
  spaceLocalAttrSaveSuccessMessage,
  spaceLocalDeleteErrorMessage,
  spaceLocalDeleteSuccessMessage,
} from "../utils";
import { useVaultData } from "../VaultProvider";
import { VaultPageHeader } from "./VaultPageHeader";
import type { SettingsTab } from "#components/DocumentTypeSettings/types";

export const SettingsRoute = ({ basePath }: { basePath: string }) => {
  const history = useHistory();
  const location = useLocation();
  const {
    spaces,
    globalLib,
    documents,
    domains,
    user,
    addGlobalAttr,
    saveGlobalAttr,
    promoteLocal,
    saveLocal,
    showToast,
    hasCapability,
  } = useVaultData();
  const [createGlobalOpen, setCreateGlobalOpen] = useState(false);
  const canManageGlobalAttributes =
    user.isSteward && hasCapability("can_manage_doc_vault_global_attribute_definitions");
  const { preflightPromotion } = usePromotionPreflight();
  const { getDeletionImpact } = useAttributeDeletionImpact();
  const { deleteSpaceLocalAttribute } = useDeleteSpaceLocalAttribute();

  const { pathname } = location;
  if (pathname === `${basePath}/settings`) {
    return <Redirect to={`${basePath}/settings/spaces`} />;
  }

  const activeTab: SettingsTab = pathname.endsWith("/global")
    ? "global"
    : pathname.endsWith("/local")
      ? "local"
      : "spaces";

  return (
    <>
      <VaultPageHeader title="Settings">
        {activeTab === "spaces" && hasCapability("can_create_doc_vault_space") ? (
          <Button
            variant="primary"
            icon="plus"
            onClick={() => history.push(`${basePath}/settings/spaces/new`)}
          >
            New space
          </Button>
        ) : null}
        {activeTab === "global" && canManageGlobalAttributes ? (
          <Button variant="primary" icon="plus" onClick={() => setCreateGlobalOpen(true)}>
            New global attribute
          </Button>
        ) : null}
      </VaultPageHeader>
      <div className={styles.content.wide}>
        <DocumentTypeSettings
          spaces={spaces}
          globalLib={globalLib}
          documents={documents}
          domains={domains}
          isSteward={user.isSteward}
          canManageGlobalAttributes={canManageGlobalAttributes}
          tab={activeTab}
          onTabChange={(t) =>
            history.push(
              t === "spaces" ? `${basePath}/settings/spaces` : `${basePath}/settings/${t}`,
            )
          }
          onEditSpace={(id) => history.push(`${basePath}/settings/spaces/${id}`)}
          onNewSpace={() => history.push(`${basePath}/settings/spaces/new`)}
          createGlobalOpen={createGlobalOpen}
          onCreateGlobalOpenChange={setCreateGlobalOpen}
          onAddGlobalAttr={async (attr) => {
            await addGlobalAttr(attr);
            showToast(`Added "${attr.name}" to the global library`);
          }}
          onSaveGlobalAttr={async (key, patch) => {
            try {
              await saveGlobalAttr(key, patch);
              showToast(globalAttrSaveSuccessMessage(patch.name ?? key));
            } catch (error) {
              // A refused NAME is reported inline by the modal, under the field
              // the steward has to change (DV-511) — a toast saying the same
              // thing elsewhere on screen is just noise. Everything else still
              // needs the toast, or the failure would be silent.
              if (!isAttributeNameConflictError(error)) {
                showToast(globalAttrSaveErrorMessage(patch.name ?? key, error), "error");
              }
              // Re-throw so the modal's submit handler keeps the modal open on failure.
              throw error;
            }
          }}
          onPromotePreflight={preflightPromotion}
          onPromoteLocal={async (spaceId, localId, newLabel) => {
            // Resolved before the write: a successful promotion refetches
            // `Spaces`, which drops the local this name came from.
            const name =
              newLabel ??
              spaces.find((s) => s.id === spaceId)?.locals.find((l) => l.id === localId)
                ?.name ??
              "attribute";
            try {
              await promoteLocal(localId, newLabel);
              showToast(globalAttrPromoteSuccessMessage(name));
            } catch (error) {
              // Same split as the save path: the name goes inline, the rest toasts.
              if (!isAttributeNameConflictError(error)) {
                showToast(globalAttrPromoteErrorMessage(name, error), "error");
              }
              // Re-throw so the promote modal stays open with the rename intact.
              throw error;
            }
          }}
          onDeletionImpact={getDeletionImpact}
          onDeleteLocal={async (_spaceId, localId, name) => {
            try {
              await deleteSpaceLocalAttribute(localId);
              showToast(spaceLocalDeleteSuccessMessage(name));
            } catch (error) {
              showToast(spaceLocalDeleteErrorMessage(name, error), "error");
              throw error;
            }
          }}
          onSaveLocal={async (spaceId, localId, patch, saved) => {
            const name = patch.name ?? saved.name;
            try {
              const wrote = await saveLocal(spaceId, localId, patch, saved);
              // Nothing was sent, so there is nothing to confirm: claiming a save
              // that never happened is worse than staying quiet. Reachable both
              // by reopening and pressing Save, and by the pre-save read finding
              // that another admin already added the value being added here.
              if (!wrote) return false;
              // `spaces` may not hold this space at all — the edited row can come
              // from a grid page that was never in it — hence the optional name.
              showToast(
                spaceLocalAttrSaveSuccessMessage(
                  name,
                  spaces.find((s) => s.id === spaceId)?.name,
                ),
              );
              return true;
            } catch (error) {
              // Toasted unconditionally, unlike the global path: this modal has
              // no inline slot wired for a server-refused name, so suppressing
              // the conflict case here would make it silent.
              showToast(spaceLocalAttrSaveErrorMessage(name, error), "error");
              // Re-throw so the edit modal stays open with the edits intact.
              throw error;
            }
          }}
          onToast={() => {}}
        />
      </div>
    </>
  );
};
